<script setup lang="ts">
import { computed } from 'vue';

import { RESOURCE_KEYS, RESOURCE_META, type ResourceKey } from '@waldermarch/shared';

import GoldCoin from '@/components/GoldCoin.vue';
import ResourceIcon from '@/components/ResourceIcon.vue';
import { useVillageStore } from '@/stores/village';
import { formatCompact, formatNumber, formatRate } from '@/utils/format';

const villages = useVillageStore();

interface Row {
  key: ResourceKey;
  label: string;
  amount: number;
  capacity: number;
  rate: number;
  fill: number;
  state: 'ok' | 'warn' | 'bad';
}

const rows = computed<Row[]>(() => {
  const capacity = villages.capacity;
  const production = villages.production;
  if (!capacity || !production) return [];

  return RESOURCE_KEYS.map((key) => {
    const amount = villages.displayResources[key];
    const cap = capacity[key];
    const rate = key === 'grain' ? production.netGrainPerHour : production.perHour[key];
    const fill = cap > 0 ? Math.min(1, amount / cap) : 0;

    // Full stores waste production; grain running negative starves the village.
    let state: Row['state'] = 'ok';
    if (rate < 0) state = 'bad';
    else if (fill > 0.95) state = 'warn';

    return {
      key,
      label: RESOURCE_META[key].displayName,
      amount,
      capacity: cap,
      rate,
      fill,
      state,
    };
  });
});

const population = computed(() => villages.population);
/** The village's coin. A currency, not a store: no capacity and no bar. */
const gold = computed(() => Math.floor(villages.current?.gold ?? 0));
const populationFill = computed(() => {
  const pop = population.value;
  if (!pop || pop.max <= 0) return 0;
  return Math.min(1, pop.used / pop.max);
});
</script>

<template>
  <div v-if="rows.length" class="resources">
    <div
      v-for="row in rows"
      :key="row.key"
      class="res"
      :class="[`res--${row.state}`, { 'res--sealed': villages.sealed }]"
      :data-tip="`${row.label}: ${formatNumber(row.amount)} of ${formatNumber(row.capacity)} in store, ${formatRate(row.rate)}.`"
    >
      <ResourceIcon class="res__icon" :name="row.key" :size="30" />
      <span class="res__figures">
        <span class="res__amount tabular">{{ formatCompact(row.amount) }}</span>
        <span class="res__rate tabular">{{ formatRate(row.rate) }}</span>
      </span>
      <span class="bar" :class="{ 'bar--warn': row.state === 'warn', 'bar--bad': row.state === 'bad' }">
        <span :style="{ width: `${row.fill * 100}%` }"></span>
      </span>
    </div>

    <div
      class="res res--gold"
      :class="{ 'res--sealed': villages.sealed }"
      :data-tip="`${formatNumber(gold)} gold in your purse, shared by every holding. Earned from bandit camps, tasks and the Seal; spent in the shop.`"
    >
      <GoldCoin class="res__icon" :size="26" />
      <span class="res__amount tabular">{{ formatCompact(gold) }}</span>
    </div>

    <div v-if="population" class="res res--pop" :class="{ 'res--sealed': villages.sealed }" :data-tip="`${population.max - population.used} roofs free. ${population.used} of ${population.max} in use: buildings and troops share them, and the Farm raises the ceiling.`">
      <ResourceIcon class="res__icon" name="population" :size="30" />
      <span class="res__figures">
        <span class="res__amount tabular">{{ population.max - population.used }}</span>
        <span class="res__rate">free</span>
      </span>
      <span class="bar" :class="{ 'bar--warn': populationFill > 0.9 }">
        <span :style="{ width: `${populationFill * 100}%` }"></span>
      </span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.resources {
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
}

/* Gold has no warehouse: no fill bar, no label, just the coin and the count. */
.res--gold {
  grid-template-rows: auto;
  min-width: 0;

  .res__amount { color: var(--gold); }
}

.res {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto auto;
  gap: 0 0.4rem;
  align-items: center;
  padding: 0.2rem 0.5rem;
  min-width: 104px;
  background: var(--stone-900);
  border: 1px solid var(--border);
  border-radius: var(--radius);

  &__icon {
    grid-row: 1 / 3;
    border-radius: 2px;
  }

  &__figures {
    display: flex;
    align-items: baseline;
    gap: 0.35rem;
  }

  &__amount { font-size: 0.9rem; }

  &__rate {
    font-size: 0.7rem;
    color: var(--parchment-faint);
  }

  .bar {
    grid-column: 2;
    width: 100%;
  }

  /*
   * The Seal gilds the frame of every tile it improves. Declared before the
   * warn and bad states so a full warehouse or a starving granary still
   * outranks the decoration.
   */
  &--sealed { border-color: #b6903f; }

  &--warn { border-color: #6a5326; }
  &--bad { border-color: #6a3128; }
  &--bad .res__rate { color: var(--bad); }
}
</style>
